import { useCallback, useEffect, useState, type ReactNode } from 'react';
import {
  ArrowRightStartOnRectangleIcon,
  Bars3Icon,
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { cn } from '../cn.js';
import { Button } from '../components/Button.js';
import { Sidebar, type NavGroup, type SidebarProps } from './Sidebar.js';
import { ThemeToggle } from './ThemeToggle.js';

export interface ShellUser {
  readonly displayName: string;
  readonly upn: string;
  readonly initials: string;
}

export interface AppShellProps {
  groups: readonly NavGroup[];
  currentPath: string;
  renderLink: SidebarProps['renderLink'];
  user: ShellUser | null;
  onSignOut: () => void;
  onOpenCommandPalette: () => void;
  children: ReactNode;
  productName?: string;
  productTagline?: string;
  buildSha?: string;
  buildTime?: string;
  timezoneLabel?: string;
  /** Rendered between the search box and the theme toggle, e.g. an env banner. */
  headerSlot?: ReactNode;
  /**
   * How the content column behaves.
   *
   * `page`, the default, is a 100rem column that scrolls -- right for every
   * page made of stacked cards, which is all of them except one.
   *
   * `app` hands the whole viewport below the header to the child and lets it
   * manage its own scrolling: no width cap, no padding, no scrollbar of its
   * own. That is what a splitter layout needs. A tool with a tree, an editor
   * and a result grid in fixed proportions cannot work inside a scrolling
   * column, because the column's height is whatever the content is and the
   * panes have nothing to divide.
   */
  variant?: 'page' | 'app';
}

/**
 * Where the rail preference lives.
 *
 * `localStorage`, unlike the console's tabs: this is a statement about the
 * shape of somebody's screen, not about their work. It holds no data, and a
 * person who wants the rail wants it tomorrow as well -- re-collapsing it on
 * every sign-in is the kind of thing that makes a tool feel like it is not
 * listening.
 */
const RAIL_STORAGE_KEY = 'csa.sidebar.rail';

function readRailPreference(): boolean {
  try {
    return window.localStorage.getItem(RAIL_STORAGE_KEY) === 'true';
  } catch {
    // Storage blocked by policy or a private window. Expanded is the right
    // default to fail to, because it is the one that shows every label.
    return false;
  }
}

/**
 * The application frame: fixed sidebar, header, scrolling content column.
 *
 * Layout matches the reference exactly -- a 15rem sidebar that collapses to an
 * overlay drawer under `lg`, and a 100rem content column. The skip link,
 * `aria-current` on the active nav item and the focusable `<main>` are all
 * load-bearing for keyboard users, not decoration.
 *
 * The sidebar also collapses to a 3.5rem icon rail on desktop, because the
 * consoles divide what is left of the viewport three or four ways and the
 * navigation is the one region in them nobody is reading.
 */
export function AppShell({
  groups,
  currentPath,
  renderLink,
  user,
  onSignOut,
  onOpenCommandPalette,
  children,
  productName = 'CSA',
  productTagline = 'Support Console',
  buildSha,
  buildTime,
  timezoneLabel,
  headerSlot,
  variant = 'page',
}: AppShellProps) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [railed, setRailed] = useState(readRailPreference);

  const toggleRail = useCallback(() => {
    setRailed((previous) => {
      const next = !previous;
      try {
        window.localStorage.setItem(RAIL_STORAGE_KEY, String(next));
      } catch {
        // Not worth surfacing: the rail still works, it just forgets.
      }
      return next;
    });
  }, []);

  // Close the mobile drawer on navigation, otherwise it covers the page the
  // user just asked for.
  useEffect(() => setDrawerOpen(false), [currentPath]);

  useEffect(() => {
    if (!drawerOpen) return;
    const onKey = (e: globalThis.KeyboardEvent): void => {
      if (e.key === 'Escape') setDrawerOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [drawerOpen]);

  const renderSidebar = (collapsed: boolean): ReactNode => (
    <Sidebar
      groups={groups}
      currentPath={currentPath}
      renderLink={renderLink}
      productName={productName}
      productTagline={productTagline}
      buildSha={buildSha}
      buildTime={buildTime}
      timezoneLabel={timezoneLabel}
      collapsed={collapsed}
    />
  );

  return (
    <div className="flex h-screen bg-sunken overflow-hidden">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:z-[60] focus:top-2 focus:left-2 focus:px-3 focus:py-2 focus:rounded-lg focus:bg-brand focus:text-on-brand focus:text-sm focus:font-medium"
      >
        Skip to content
      </a>

      <aside
        id="main-navigation"
        className={cn(
          'hidden lg:flex flex-col shrink-0 border-r border-line transition-[width] duration-150',
          railed ? 'w-14' : 'w-60',
        )}
      >
        {renderSidebar(railed)}
      </aside>

      {drawerOpen ? (
        <div className="lg:hidden fixed inset-0 z-40 flex">
          <div
            className="absolute inset-0 bg-overlay/70 animate-fade-in"
            onClick={() => setDrawerOpen(false)}
            aria-hidden="true"
          />
          <aside
            className="relative w-60 h-full border-r border-line animate-slide-up"
            aria-label="Main navigation"
          >
            <div className="absolute top-3 right-3 z-10">
              <Button
                variant="ghost"
                size="xs"
                iconOnly
                aria-label="Close menu"
                onClick={() => setDrawerOpen(false)}
                leadingIcon={<XMarkIcon className="w-4 h-4" aria-hidden="true" />}
              />
            </div>
            {/*
              Never railed. The drawer floats over the page instead of taking
              a column beside it, so icons would save no space at all and
              would cost every label.
            */}
            {renderSidebar(false)}
          </aside>
        </div>
      ) : null}

      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <header className="flex items-center gap-3 px-4 lg:px-6 py-2.5 bg-surface border-b border-line shrink-0">
          <div className="lg:hidden">
            <Button
              variant="ghost"
              size="sm"
              iconOnly
              aria-label="Open menu"
              aria-expanded={drawerOpen}
              onClick={() => setDrawerOpen(true)}
              leadingIcon={<Bars3Icon className="w-4 h-4" aria-hidden="true" />}
            />
          </div>
          <span className="lg:hidden font-semibold text-body">{productName}</span>

          {/*
            In the header rather than inside the sidebar, because a control
            that lives in the thing it shrinks ends up 3.5rem wide and
            unlabelled once it has been used. Here it stays the same size and
            in the same place in both states.
          */}
          <div className="hidden lg:block">
            <Button
              variant="ghost"
              size="sm"
              iconOnly
              aria-expanded={!railed}
              aria-controls="main-navigation"
              aria-label={railed ? 'Expand the menu' : 'Collapse the menu to icons'}
              title={railed ? 'Expand the menu' : 'Collapse the menu to icons'}
              onClick={toggleRail}
              leadingIcon={
                railed ? (
                  <ChevronDoubleRightIcon className="w-4 h-4" aria-hidden="true" />
                ) : (
                  <ChevronDoubleLeftIcon className="w-4 h-4" aria-hidden="true" />
                )
              }
            />
          </div>

          <button
            type="button"
            onClick={onOpenCommandPalette}
            className={cn(
              'hidden lg:flex items-center gap-2 w-72 px-3 py-1.5 rounded-lg',
              'border border-line-strong bg-sunken text-sm text-subtle',
              'hover:border-brand/60 hover:text-muted transition-colors',
            )}
          >
            <MagnifyingGlassIcon className="w-4 h-4 shrink-0" aria-hidden="true" />
            <span className="flex-1 text-left">Search pages&hellip;</span>
            <kbd className="text-[10px] font-mono border border-line rounded px-1 py-0.5">
              Ctrl K
            </kbd>
          </button>

          <div className="flex-1" />

          {headerSlot}
          <ThemeToggle />

          {user ? (
            <div className="flex items-center gap-2 pl-3 border-l border-line">
              <div
                className="shrink-0 w-8 h-8 rounded-full bg-brand text-on-brand flex items-center justify-center text-xs font-bold"
                aria-hidden="true"
              >
                {user.initials}
              </div>
              <div className="hidden xl:block min-w-0 max-w-[12rem]">
                <p className="text-xs font-medium text-body truncate">{user.displayName}</p>
                <p className="text-[11px] text-subtle truncate">{user.upn}</p>
              </div>
              <Button
                variant="ghost"
                size="xs"
                iconOnly
                aria-label="Sign out"
                title="Sign out"
                onClick={onSignOut}
                leadingIcon={
                  <ArrowRightStartOnRectangleIcon className="w-4 h-4" aria-hidden="true" />
                }
              />
            </div>
          ) : null}
        </header>

        {/* tabIndex=-1 makes the skip link's target focusable, so activating
            it actually moves the keyboard cursor rather than just scrolling. */}
        {variant === 'app' ? (
          /**
           * `min-h-0` is the load-bearing class and the easiest one to drop.
           * A flex child's default minimum height is its content, so without
           * it the child grows past the viewport instead of being constrained
           * by it -- and `overflow-hidden` on something taller than the
           * screen clips the bottom off rather than scrolling to it.
           */
          <main
            id="main"
            tabIndex={-1}
            className="flex min-h-0 flex-1 flex-col overflow-hidden focus:outline-none"
          >
            {children}
          </main>
        ) : (
          <main id="main" tabIndex={-1} className="flex-1 overflow-y-auto focus:outline-none">
            <div className="max-w-content mx-auto p-4 lg:p-6">{children}</div>
          </main>
        )}
      </div>
    </div>
  );
}

export interface PageHeaderProps {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  breadcrumbs?: ReactNode;
}

export function PageHeader({ title, description, actions, breadcrumbs }: PageHeaderProps) {
  return (
    <div className="flex flex-col gap-2">
      {breadcrumbs}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="min-w-0">
          <h1 className="text-2xl font-bold text-body break-words">{title}</h1>
          {description ? <p className="text-sm text-muted mt-1">{description}</p> : null}
        </div>
        {actions ? (
          <div className="flex items-center gap-2 flex-wrap shrink-0">{actions}</div>
        ) : null}
      </div>
    </div>
  );
}
